import {
  type Channel,
  type ChannelCreate,
  type ChannelMember,
  type ChatAttachmentDownloadResponse,
  type ChatAttachmentUploadRequest,
  type ChatAttachmentUploadResponse,
  type ChatInboxResponse,
  type ChatMessage,
  type ChatMessageCreate,
  type ChatReaction,
  type ChatSearchResponse,
  type LinkUnfurlResponse,
  type MentionSuggestResponse,
  type MentionSuggestion,
  type NotificationLevel,
  ChannelListResponseSchema,
  ChatInboxResponseSchema,
  ChatMessageListResponseSchema,
  ChatSearchResponseSchema,
  LinkUnfurlResponseSchema,
  MentionSuggestResponseSchema,
} from "@l30/shared";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  buildOptimisticMessage,
  insertOptimisticMessage,
  nextBeforeCursor,
  type MessagePages,
} from "../lib/chat-messages.js";

import { apiFetch, apiFetchUnchecked } from "./client.js";

const CHANNELS_KEY = ["channels"] as const;
const MESSAGES_KEY = ["messages"] as const;
const UNREAD_KEY = ["unread"] as const;
const INBOX_KEY = ["chat-inbox"] as const;

export function useChannels() {
  return useQuery({
    queryKey: CHANNELS_KEY,
    queryFn: () => apiFetch("/messages/channels", {}, ChannelListResponseSchema),
  });
}

export function useCreateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ChannelCreate) =>
      apiFetchUnchecked<Channel>("/messages/channels", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHANNELS_KEY }),
  });
}

/**
 * Channel (or thread) history, cursor-paginated for scroll-back (assessment
 * U8). The server returns the newest `limit` messages plus `hasMore` and
 * accepts a `before` ISO cursor; `useInfiniteQuery` drives "load older".
 * Flatten `data.pages` with `flattenMessagePages` before rendering.
 */
export function useMessages(channelId: string, threadRootId?: string) {
  return useInfiniteQuery({
    queryKey: [...MESSAGES_KEY, channelId, threadRootId],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ channelId });
      if (threadRootId) params.set("threadRootId", threadRootId);
      if (pageParam) params.set("before", pageParam);
      return apiFetch(`/messages?${params.toString()}`, {}, ChatMessageListResponseSchema);
    },
    enabled: !!channelId,
    getNextPageParam: nextBeforeCursor,
    // 5s poll is the WebSocket fallback — when WS is healthy, the
    // broadcast invalidates this key and the poll is just a backstop.
    refetchInterval: 5000,
  });
}

/**
 * Send a message. Optimistic (assessment U8): the message renders the instant
 * Send is pressed, then reconciles against the server on settle, or rolls back
 * on error. Pass the current user's email so the placeholder shows the right
 * author; omit it (e.g. non-chat callers) to fall back to a plain send.
 */
export function useSendMessage(currentUserEmail?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ChatMessageCreate) =>
      apiFetchUnchecked<ChatMessage>("/messages", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onMutate: async (variables) => {
      if (!currentUserEmail) return { key: undefined, previous: undefined };
      const key = [...MESSAGES_KEY, variables.channelId, variables.threadRootId ?? undefined];
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<MessagePages>(key);
      const optimistic = buildOptimisticMessage({
        id: `optimistic-${crypto.randomUUID()}`,
        channelId: variables.channelId,
        threadRootId: variables.threadRootId ?? null,
        authorEmail: currentUserEmail,
        body: variables.body,
        attachments: variables.attachments ?? [],
        createdAt: new Date().toISOString(),
      });
      qc.setQueryData<MessagePages>(key, (data) => insertOptimisticMessage(data, optimistic));
      return { key, previous };
    },
    onError: (_err, _variables, context) => {
      // Roll back the optimistic insert; the global MutationCache surfaces the toast.
      if (context?.key && context.previous !== undefined) {
        qc.setQueryData(context.key, context.previous);
      }
    },
    onSettled: (_data, _err, variables) => {
      // Refetch to swap the temp message for the server's canonical row.
      qc.invalidateQueries({ queryKey: [...MESSAGES_KEY, variables.channelId] });
    },
  });
}

export function useEditMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      apiFetchUnchecked<ChatMessage>(`/messages/${id}`, {
        method: "PUT",
        body: JSON.stringify({ body }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MESSAGES_KEY }),
  });
}

export function useDeleteMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetchUnchecked<void>(`/messages/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MESSAGES_KEY }),
  });
}

export function useMarkAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) =>
      apiFetchUnchecked<void>(`/messages/channels/${channelId}/read`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: UNREAD_KEY }),
  });
}

export function useUnreadCounts() {
  return useQuery({
    queryKey: UNREAD_KEY,
    queryFn: () => apiFetchUnchecked<{ unread: Record<string, number> }>("/messages/unread"),
    refetchInterval: 30_000,
  });
}

// ─── Reactions (Phase C) ───────────────────────────────────

export function useAddReaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      apiFetchUnchecked<ChatReaction>(`/messages/${messageId}/reactions`, {
        method: "POST",
        body: JSON.stringify({ emoji }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MESSAGES_KEY }),
  });
}

export function useRemoveReaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      apiFetchUnchecked<void>(`/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MESSAGES_KEY }),
  });
}

// ─── Cross-channel inbox (Phase C) ─────────────────────────

export function useChatInbox() {
  return useQuery<ChatInboxResponse>({
    queryKey: INBOX_KEY,
    queryFn: () => apiFetch("/messages/inbox", {}, ChatInboxResponseSchema),
    refetchInterval: 30_000,
  });
}

export function useMarkInboxNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) =>
      apiFetchUnchecked<void>(`/messages/inbox/${notificationId}/read`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: INBOX_KEY }),
  });
}

export function useMarkChannelInboxRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) =>
      apiFetchUnchecked<void>(`/messages/channels/${channelId}/inbox/read`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: INBOX_KEY }),
  });
}

// ─── Composer polish (Phase H) ─────────────────────────────

/** Mention autocomplete — fires on every keystroke after `@` or `#`.
 *  TanStack Query handles dedupe + caching; a 150 ms debounce on the
 *  caller side keeps the request volume bounded. */
export function useMentionSuggest(args: {
  channelId: string;
  prefix: "@" | "#";
  q: string;
  enabled: boolean;
}) {
  return useQuery<MentionSuggestResponse>({
    queryKey: ["chat-mention-suggest", args.channelId, args.prefix, args.q],
    queryFn: () => {
      const params = new URLSearchParams({
        channelId: args.channelId,
        prefix: args.prefix,
        q: args.q,
        limit: "8",
      });
      return apiFetch(
        `/messages/mentions/suggest?${params.toString()}`,
        {},
        MentionSuggestResponseSchema,
      );
    },
    enabled: args.enabled && args.channelId.length > 0,
    // Mention suggestions are stable for the keystroke window.
    staleTime: 5_000,
  });
}

export type { MentionSuggestion };

/** Resolve a URL preview card for a pasted link. Best-effort: server
 *  may return an empty payload (null fields) for unfurl-unfriendly
 *  targets, in which case the caller renders a plain link. */
export async function getLinkUnfurl(url: string): Promise<LinkUnfurlResponse> {
  const params = new URLSearchParams({ url });
  return apiFetch(`/messages/unfurl?${params.toString()}`, {}, LinkUnfurlResponseSchema);
}

/** TanStack-cached unfurl. Same URL across many messages → one fetch.
 *  Long staleTime: unfurls are cosmetic; serving a slightly stale OG
 *  preview is fine, and keeps us off the target's rate-limit. */
export function useLinkUnfurl(url: string | null) {
  return useQuery<LinkUnfurlResponse>({
    queryKey: ["chat-link-unfurl", url],
    queryFn: () => getLinkUnfurl(url ?? ""),
    enabled: !!url,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 1 day
    retry: false,
  });
}

// ─── Attachments (Phase E) ─────────────────────────────────

/**
 * Two-step upload flow:
 *   1. POST /messages/attachments/upload-url with metadata → get a
 *      short-TTL signed PUT URL + storage key
 *   2. PUT the file bytes directly to that URL with the matching
 *      Content-Type and `x-ms-blob-type: BlockBlob` header
 * Returns the storage key once the upload completes — caller adds it
 * to `attachments` on the message they're about to send.
 */
export async function uploadChatAttachment(
  channelId: string,
  file: File,
): Promise<{ key: string; filename: string; contentType: string; size: number }> {
  const meta: ChatAttachmentUploadRequest = {
    channelId,
    filename: file.name,
    contentType: file.type as ChatAttachmentUploadRequest["contentType"],
    size: file.size,
  };
  const signed = await apiFetchUnchecked<ChatAttachmentUploadResponse>(
    "/messages/attachments/upload-url",
    {
      method: "POST",
      body: JSON.stringify(meta),
    },
  );

  const putRes = await fetch(signed.uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type,
      "x-ms-blob-type": "BlockBlob",
    },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`Attachment upload failed: ${putRes.status} ${putRes.statusText}`);
  }

  return { key: signed.key, filename: file.name, contentType: file.type, size: file.size };
}

/** Resolve a signed download URL for an attachment on a message.
 *  Server re-checks ACL on every call; returned URL has a short TTL. */
export async function getChatAttachmentDownloadUrl(
  messageId: string,
  key: string,
): Promise<string> {
  const params = new URLSearchParams({ key });
  const res = await apiFetchUnchecked<ChatAttachmentDownloadResponse>(
    `/messages/${messageId}/attachments/download-url?${params.toString()}`,
  );
  return res.downloadUrl;
}

// ─── FTS search (Phase D) ──────────────────────────────────

export function useChatSearch(
  q: string,
  filters: { channelId?: string; authorEmail?: string; before?: string; after?: string } = {},
) {
  return useQuery<ChatSearchResponse>({
    queryKey: ["chat-search", q, filters],
    queryFn: () => {
      const params = new URLSearchParams({ q });
      if (filters.channelId) params.set("channelId", filters.channelId);
      if (filters.authorEmail) params.set("authorEmail", filters.authorEmail);
      if (filters.before) params.set("before", filters.before);
      if (filters.after) params.set("after", filters.after);
      return apiFetch(`/messages/search?${params.toString()}`, {}, ChatSearchResponseSchema);
    },
    enabled: q.trim().length > 0,
    // Search is user-driven; cache aggressively. Re-fetch if query
    // changes (queryKey takes care of that).
    staleTime: 30_000,
  });
}

// ─── Per-channel notification settings (Phase C) ───────────

export function useUpdateChannelMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      patch,
    }: {
      channelId: string;
      patch: { notificationLevel?: NotificationLevel; mutedUntil?: string | null };
    }) =>
      apiFetchUnchecked<ChannelMember>(`/messages/channels/${channelId}/membership`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHANNELS_KEY });
      qc.invalidateQueries({ queryKey: INBOX_KEY });
    },
  });
}
